import React, { useEffect } from "react";

import { ZodProvider } from "@autoform/zod";
import {
  useNavigation,
  useNotification,
  useParsed,
} from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { DeleteButton } from "@/components/refine-ui/buttons/delete";
import { AutoForm } from "@/components/ui/autoform";
import { injectCustomAutoFormComponents } from "@/components/ui/autoform/components/custom";
import { Button } from "@/components/ui/button";
import { useLoadingOverlay } from "@/contexts/loading-context";
import { toHumanErrorMessage } from "@/lib/extensions/httpErrorExtension";
import { useTagRulesRepository } from "@/pages/tina/tag-rules/hooks/use-tag-rules-repository";
import {
  TagRuleEditFormType,
  tagRuleEditSchema,
} from "@/pages/tina/tag-rules/schemas/tag-rule.form";

const schemaProvider = new ZodProvider(tagRuleEditSchema);

export const TagRulesEdit = () => {
  const { action, id } = useParsed();
  const { get, create, update } = useTagRulesRepository();
  const [values, setValues] = React.useState<TagRuleEditFormType>();
  const { open } = useNotification();
  const { setLoading } = useLoadingOverlay();
  const { list } = useNavigation();

  const setTitle = useDocumentTitle();

  useEffect(() => {
    if (!id) {
      setTitle("Create Tag Rule");
      return;
    }
    setLoading(true);
    get(id.toString())
      .then((response) => {
        const data = tagRuleEditSchema.parse(response);
        setValues(data);
        setTitle("Edit " + data.tag_name);
      })
      .catch((error) => {
        console.error(error);
        open?.({
          type: "error",
          message: "Failed to get tag rule",
          description: toHumanErrorMessage(error),
        });
      })
      .finally(() => setLoading(false));
  }, [id]);

  const onSubmit = async (submitted: TagRuleEditFormType) => {
    if (!id) {
      await create(submitted);
    } else {
      await update({ ...submitted, id: id.toString() });
    }
  };

  return (
    <div className="flex flex-row">
      <div className="w-3/4 p-8 flex flex-col space-y-4">
        {(values || action === "create") && (
          <AutoForm
            schema={schemaProvider}
            defaultValues={values}
            onSubmit={onSubmit}
            {...injectCustomAutoFormComponents()}
          >
            <BottomActionBar className="justify-between">
              <Button type="submit">Submit</Button>
              {id && (
                <DeleteButton
                  resource="tina-tag-rules"
                  id={id.toString()}
                  onSuccess={() => {
                    list("tina-tag-rules");
                  }}
                />
              )}
            </BottomActionBar>
          </AutoForm>
        )}
      </div>
    </div>
  );
};
